import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PeSignupStatus } from '../pe-signup-status';
import { catchError, map, tap } from 'rxjs/operators';
import { EyefinityDashboardStatusTypeEnum } from '../../eyefinity-dashboard/eyefinity-dashboard-status-type.enum';
import { PeSignupState } from '../../dashboard/dashboard/pe-type.enum';
import { API_SIGNUP_STATUS, PECONN_TOKEN } from '@share/constants';
import { AuthenticationService } from '../authentication/authentication.service';
import { NGXLogger } from 'ngx-logger';
import { mockPeSignupStatus } from '@share/constants-mock-database';

export const SIGNUP_STATUS_EWEBEXTRA = 'ewebextra';
export const SIGNUP_STATUS_OMI = 'omi';
export const SIGNUP_STATUS_PATIENT_NOTIFICATIONS = 'patientNotifications';
export const SHOULD_CHECK_FOR_CHANGES = 'shouldCheckForChanges';

@Injectable({
  providedIn: 'root',
})
export class SignupStatusService {
  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private logger: NGXLogger,
  ) {}

  /**
   * Using an efan try to figure out the state of the practice's ewebextra state
   * @param efan
   */
  checkForEyefinityDashboardStatus$(
    efan?: string,
  ): Observable<EyefinityDashboardStatusTypeEnum> {
    if (!efan || efan.length === 0) {
      efan = this.authService.getPeConnAccessToken().efan;
    }
    this.logger.debug('In eyefinityDashboardStatus$:');
    const url = environment.queryUrl + API_SIGNUP_STATUS + '?efan=' + efan;
    if (environment.envName === 'dev' && environment.mockDatabase) {
      return of(EyefinityDashboardStatusTypeEnum.newSignup);
    } else {
      return this.http.get<PeSignupStatus>(url).pipe(
        map((result) => {
          switch (result[SIGNUP_STATUS_OMI]) {
            case PeSignupState.SignUp:
              return EyefinityDashboardStatusTypeEnum.newSignup;
            case PeSignupState.Pending:
              return EyefinityDashboardStatusTypeEnum.pendingSignup;
            case PeSignupState.Manage:
              return EyefinityDashboardStatusTypeEnum.omiManage;
            default:
              return EyefinityDashboardStatusTypeEnum.newSignup;
          }
        }),
        catchError(this.handleError),
      );
    }
  }

  /**
   * Returns the PeSignupStatus as it exists in sessionStorage rather than re-querying the server
   */
  getCachedStatusForEpm(): PeSignupStatus {
    const token = this.authService.getPeConnAccessToken();
    if (!token.signupStatusEwx && !token.signupStatusEpn) {
      this.initSignupStatus$().subscribe((result) => {
        return {
          ...new PeSignupStatus(),
          ewebextra: result.ewebextra,
          patientNotifications: result.patientNotifications,
        };
      });
    } else {
      return {
        ...new PeSignupStatus(),
        ewebextra: token.signupStatusEwx,
        patientNotifications: token.signupStatusEpn,
      };
    }
  }

  /**
   * This needs to be called first to fetch signup status info and pass the observable to the component.
   * @returns {Observable<PeSignupStatus>}
   */
  initSignupStatus$(): Observable<PeSignupStatus> {
    this.logger.debug('In initSignupStatus$:');
    const epmCompany = this.authService.getPeConnAccessToken().companyId
      ? '?epmCompanyId=' + this.authService.getPeConnAccessToken().companyId
      : '';
    let efan: string;
    if (epmCompany && epmCompany.length > 0) {
      efan = this.authService.getPeConnAccessToken().efan
        ? '&efan=' + this.authService.getPeConnAccessToken().efan
        : '';
    } else {
      efan = this.authService.getPeConnAccessToken().efan
        ? '?efan=' + this.authService.getPeConnAccessToken().efan
        : '';
    }
    const url = environment.queryUrl + API_SIGNUP_STATUS + epmCompany + efan;
    if (environment.envName === 'dev' && environment.mockDatabase) {
      return of(mockPeSignupStatus());
    } else {
      return this.http.get<PeSignupStatus>(url).pipe(
        tap((result) => {
          if (result && result.patientNotifications && result.ewebextra) {
            const token = {
              ...this.authService.getPeConnAccessToken(),
              signupStatusEpn: result.patientNotifications,
              signupStatusEwx: result.ewebextra,
            };
            this.authService.addToSessionStorage(PECONN_TOKEN, token);
          }
        }),
        catchError(this.handleError),
      );
    }
  }

  private handleError(error: any): Observable<any> {
    this.logger.error('An error occurred', error);
    return throwError(error || 'Server error is Sign Up Service');
  }
}
